"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as THREE from "three";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";

// --- CUSTOM SHADERS FOR BLUEPRINT EFFECT ---
const vertexShader = `
  varying vec3 vPosition;
  void main() {
    vPosition = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShaderWireframe = `
  uniform float uProgress;
  uniform vec3 uColor;
  varying vec3 vPosition;

  void main() {
    float height = (vPosition.y + 2.5) / 5.0;
    float scan = height + (vPosition.x * 0.1); 
    float visible = step(scan, uProgress * 1.2); 

    if (visible < 0.5) discard;

    float tip = 1.0 - smoothstep(0.0, 0.05, (uProgress * 1.2) - scan);
    vec3 finalColor = uColor + (vec3(1.0) * tip); 
    
    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

const fragmentShaderSolid = `
  uniform float uProgress;
  uniform vec3 uColor;
  varying vec3 vPosition;

  void main() {
    float height = (vPosition.y + 2.5) / 5.0;
    float scan = height + (vPosition.x * 0.1); 
    float delayedProgress = (uProgress - 0.2) * 1.25;
    float visible = step(scan, delayedProgress); 

    if (visible < 0.5) discard;

    gl_FragColor = vec4(uColor, 0.65); 
  }
`;

export default function CestusLoader({ onComplete }: { onComplete: () => void }) {
  const [progress, setProgress] = useState(0);
  const [glitchActive, setGlitchActive] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const progressRef = useRef(0);

  // Target values to match Global3DBackground
  const baseScaleRef = useRef(1);
  const finalScaleRef = useRef(1);
  const isMobileRef = useRef(false);
  const readyRef = useRef(false);

  useEffect(() => {
    progressRef.current = progress / 100;
  }, [progress]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMounted(true);
    isMobileRef.current = window.innerWidth < 768;
    
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          // Increased delay to 1200ms to allow the 3D transition to finish visually
          // before we unmount the component
          setTimeout(onComplete, 1200); 
          return 100;
        }
        
        let inc = 0;
        if (prev < 30) inc = Math.random() * 15;
        else if (prev < 70) inc = Math.random() * 5;
        else inc = Math.random() * 20;
        
        return Math.min(prev + inc, 100);
      });
    }, 60);

    const glitchInterval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 80);
    }, 1800);

    return () => {
      clearInterval(timer);
      clearInterval(glitchInterval);
    };
  }, [onComplete]);

  useEffect(() => {
    if (!isMounted || !canvasRef.current) return;

    const scene = new THREE.Scene();
    sceneRef.current = scene;
    
    const camera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.set(0, 0, 10);

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    rendererRef.current = renderer;
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    
    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambient);

    const keyLight = new THREE.DirectionalLight(0xffffff, 2.2);
    keyLight.position.set(5, 5, 5);
    scene.add(keyLight);

    const rimLightA = new THREE.SpotLight(0x22d3ee, 55);
    rimLightA.position.set(-5, 5, -5);
    rimLightA.lookAt(0, 0, 0);
    scene.add(rimLightA);

    const rimLightB = new THREE.SpotLight(0x0066ff, 65);
    rimLightB.position.set(5, -2, -5);
    rimLightB.lookAt(0, 0, 0);
    scene.add(rimLightB);
    renderer.compile(scene, camera);

    const materials: THREE.ShaderMaterial[] = [];
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    
    let mainGroup: THREE.Group | null = null;

    loader.load(
      "/GPU_mobile.glb",
      (gltf) => {
        const model = gltf.scene;
        
        // --- SCALE CALCULATION ---
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);

        // Match Global3DBackground sizing exactly
        const targetSizeLoader = isMobileRef.current ? 2.8 : 4.5;
        const targetSizeFinal  = isMobileRef.current ? 3.5 : 6.0;

        const scaleLoader = targetSizeLoader / maxDim;
        const scaleFinal  = targetSizeFinal  / maxDim;

        // Store both scales
        baseScaleRef.current  = scaleLoader;
        finalScaleRef.current = scaleFinal;

        const center = box.getCenter(new THREE.Vector3());
        model.position.set(-center.x, -center.y, -center.z);
        
        mainGroup = new THREE.Group();
        mainGroup.scale.setScalar(scaleLoader);
        
        // Initial "Blueprint" Orientation
        mainGroup.rotation.x = 0.2; 
        mainGroup.rotation.y = -0.5;

        // Apply Custom "Blueprint" Materials
        model.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            mesh.renderOrder = 1;
        
            const wireframeMaterial = new THREE.ShaderMaterial({
              vertexShader,
              fragmentShader: fragmentShaderWireframe,
              uniforms: {
                uProgress: { value: 0 },
                uColor: { value: new THREE.Color(0x00aaff) }
              },
              transparent: true,
              depthWrite: false,
              blending: THREE.AdditiveBlending,
            });
            materials.push(wireframeMaterial);
        
            // 🔥 DEFER HEAVY EDGE CREATION BY 1 FRAME
            requestAnimationFrame(() => {
              const edges = new THREE.EdgesGeometry(mesh.geometry, 20);
              const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
              wireframe.renderOrder = 999;
              mesh.add(wireframe);
            });      

            const solidMaterial = new THREE.ShaderMaterial({
              vertexShader,
              fragmentShader: fragmentShaderSolid,
              uniforms: {
                uProgress: { value: 0 },
                uColor: { value: new THREE.Color(0x003366) }
              },
              transparent: true,
              depthWrite: true,
              blending: THREE.NormalBlending,
              side: THREE.DoubleSide 
            });
            mesh.material = solidMaterial;
            materials.push(solidMaterial);
          }
        });
        
        // Fix Internal Rotation (Same as Global3DBackground)
        model.rotation.z -= Math.PI;
        
        mainGroup.add(model);
        scene.add(mainGroup);
        readyRef.current = true;
        clock.start();
      },
      undefined,
      (error) => console.error("Error loading GPU:", error)
    );

    const clock = new THREE.Clock(false);
    let rafId = 0;
    const animate = () => {
      if (!readyRef.current) {
        rafId = requestAnimationFrame(animate);
        return;
      }
      const elapsedTime = clock.getElapsedTime();
      const currentProgress = progressRef.current;

      materials.forEach(mat => {
        if (mat.uniforms.uProgress) {
          mat.uniforms.uProgress.value = currentProgress;
        }
      });

      if (mainGroup) {
        // --- ANIMATION LOGIC ---

        if (currentProgress < 1.0) {
            // PHASE 1: LOADING
            // Gentle float while loading
            mainGroup.position.y = Math.sin(elapsedTime * 0.8) * 0.15;
        } else {
            // PHASE 2: TRANSITION TO BACKGROUND STATE
            // We want to match the Global3DBackground initial state:
            // Rotation: (0, 0, 0)
            // Position: (0, 0, 0)
            // Scale: Target Size 6.0 (Loader is 4.5, so we scale up ~1.33x)
            
            const lerpFactor = 0.08; // Speed of transition

            // 1. Rotate to flat (0,0,0)
            mainGroup.rotation.x = THREE.MathUtils.lerp(mainGroup.rotation.x, 0, lerpFactor);
            mainGroup.rotation.y = THREE.MathUtils.lerp(mainGroup.rotation.y, 0, lerpFactor);
            mainGroup.rotation.z = THREE.MathUtils.lerp(mainGroup.rotation.z, 0, lerpFactor);

            // 2. Move to Center (0,0,0)
            mainGroup.position.y = THREE.MathUtils.lerp(mainGroup.position.y, 0, lerpFactor);
            
            // 3. Scale Up
            // Global3D uses targetSize 6.0 (desktop) or 3.5 (mobile). 
            // Assuming desktop for the main transition or averaging.
            // 6.0 / 4.5 = 1.333 ratio.
            // 3. Scale to final Global3DBackground size
            const currentScale = mainGroup.scale.x;
            const newScale = THREE.MathUtils.lerp(
              currentScale,
              finalScaleRef.current,
              lerpFactor
            );
            mainGroup.scale.setScalar(newScale);
        }
      }

      rimLightA.intensity = 55 + Math.cos(elapsedTime * 1.8) * 10;
      rimLightB.intensity = 65 + Math.sin(elapsedTime * 2) * 20;

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    };
    
    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      // The loop is stopped before the renderer goes: a frame that lands after
      // dispose makes three re-upload textures GL still holds, which Safari
      // reports as "glTexStorage2D: Texture is immutable".
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleResize);
      renderer.dispose();
      scene.clear();
    };
  }, [isMounted]); 

  const [backgroundData, setBackgroundData] = useState<string[]>([]);
  useEffect(() => {
    if (!isMounted) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setBackgroundData(
      Array.from({ length: 80 }).map(() =>
        Math.random().toString(16).toUpperCase().substring(2, 12)
      )
    );
  }, [isMounted]);

  if (!isMounted) return null;

  return (
    <motion.div
      exit={{ 
        opacity: 0, 
        // We disable the scale/blur exit animation here because 
        // the 3D model is doing the transition visually.
        // We just want a clean fade out of the UI elements.
      }}
      transition={{ duration: 0.8 }}
      className="fixed inset-0 z-[100] bg-[#010206] flex items-center justify-center overflow-hidden"
    >
      {/* GPU WIREFRAME CANVAS */}
      <canvas 
        ref={canvasRef} 
        className="absolute inset-0 z-[5] pointer-events-none opacity-100" 
      />

      {/* 1. KINETIC DATA WALL */}
      <motion.div 
        exit={{ opacity: 0 }}
        className="absolute inset-0 z-0 opacity-[0.04] select-none pointer-events-none font-mono text-[10px] flex flex-wrap gap-x-4 gap-y-2 p-4 overflow-hidden"
      >
        {backgroundData.map((hex, i) => (
          <span key={i} className="whitespace-nowrap text-blue-500">
            0x{hex} {'//'} {i.toString(16).padStart(3, '0')}
          </span>
        ))}
      </motion.div>

      {/* 2. THE HERO SECTION (UI Elements Fade Out on Exit) */}
      <motion.div 
        exit={{ opacity: 0, y: -20, filter: "blur(10px)" }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full flex flex-col items-center"
      >
        
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full text-center">
           <div className="w-[60vw] h-[20vw] bg-blue-600/20 blur-[120px] rounded-full mx-auto animate-pulse" />
        </div>

        <div className="relative overflow-visible">
        <motion.h1 
        layoutId="main-title"
        style={{ 
            clipPath: `inset(0 ${100 - progress}% 0 0)`,
        }}
        className="text-[24vw] md:text-[20vw] font-black leading-none tracking-tighter uppercase text-white relative z-20"
        >
        CESTUS
        </motion.h1>

          <AnimatePresence>
            {glitchActive && (
              <>
                <motion.h1 
                  initial={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  className="absolute top-0 left-2 text-[24vw] md:text-[20vw] font-black leading-none tracking-tighter uppercase text-red-500/75 -z-10 mix-blend-screen"
                >
                  CESTUS
                </motion.h1>
                <motion.h1 
                  initial={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  className="absolute top-0 -left-2 text-[24vw] md:text-[20vw] font-black leading-none tracking-tighter uppercase text-cyan-500/75 -z-10 mix-blend-screen"
                >
                  CESTUS
                </motion.h1>
              </>
            )}
          </AnimatePresence>

          <h1 className="absolute top-0 left-0 text-[24vw] md:text-[20vw] font-black leading-none tracking-tighter uppercase text-white/5 -z-20" 
              style={{ WebkitTextStroke: '1px rgba(255,255,255,0.08)' }}>
            CESTUS
          </h1>
        </div>

        {/* 3. SUB-INTERFACE */}
        <div className="mt-[-2vw] flex flex-col items-center gap-6 w-full max-w-xl px-12">
            <div className="flex justify-between w-full font-mono text-[10px] tracking-[0.5em] text-blue-400">
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-ping" />
                  ANALYZING_CORE
                </span>
                <span className="text-white/80">{Math.floor(progress)}%</span>
            </div>

            <div className="relative h-[1px] w-full bg-white/5">
                <motion.div 
                    className="absolute top-0 left-0 h-full w-full origin-left bg-gradient-to-r from-blue-600 to-white shadow-[0_0_25px_#3b82f6]"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: progress / 100 }}
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                />
            </div>

            <div className="flex justify-between w-full font-mono text-[10px] text-white/75 uppercase tracking-[0.2em]">
                <span>Mem_Alloc: 0x882F</span>
                <span>Substrate: Active</span>
                <span>Auth: Secured</span>
            </div>
        </div>
      </motion.div>

      <svg className="absolute hidden">
        <defs>
          <filter id="glitch-filter">
            <feTurbulence type="fractalNoise" baseFrequency="0.02 0.15" numOctaves="2" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale={glitchActive ? 35 : 0} xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
      </svg>

      <div className="absolute inset-0 pointer-events-none z-[60] bg-[radial-gradient(circle_at_center,transparent_20%,#010206_100%)]" />
      <div className="absolute inset-0 pointer-events-none z-[70] opacity-[0.08] bg-[url('/noise.svg')]" />
    </motion.div>
  );
}
